/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    Action,
    ActionKernelType,
    ActionList,
    InfoCardKernel,
    InfoCardKernelType,
    Provider,
    UnifiedCollectionInfoCardKernel,
    VodOfferInfoCardKernel,
    WatchFromMyShowsActionKernel,
    WatchLiveActionKernel,
    WatchNowAction,
} from 'client-code/actions';
import {
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemType,
} from 'client-code/vod-browse';

import { InfoCardUserAction } from '../../views/common/InfoCard/InfoCardUserAction';
import { Locale, LocaleKeys } from '../locale';

// eslint-disable-next-line no-unused-vars, no-shadow
export enum InfoCardOverlayOptionsType {
    BOOKMARK,
    SEE_MORE_INFO,
    WATCH_FROM_LIVE,
    WATCH_NOW,
    CREATE_ONEPASS,
    MODIFY_ONEPASS,
    RECORD_OPTION,
}
export class MetadataOperations {
    /**
     * Maps a VOD item to its InfoCardKernel.
     * - UnifiedCollectionVodItem -> UnifiedCollectionInfoCardKernel
     * - Episode/Movie/Special -> VodOfferInfoCardKernel
     * @param item VOD item with its concrete kernel payload.
     * @returns InfoCardKernel for the InfoCard.
     * @throws {TypeError} If item or vodItemType is missing, or type is unknown.
     */
    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernelByVodItem(
        item: VodItem & (UnifiedCollectionVodItemKernel | OfferVodItemKernel),
    ): InfoCardKernel {
        // TODO:Need to validate the implementation after fix of https://jira.xperi.com/browse/CLOUD-31485
        if (!item || !item.vodItemType) {
            throw new TypeError('InvalidVodItem: Cannot read properties of undefined');
        }

        const type = item.vodItemType;

        switch (type) {
            case VodItemType.UnifiedCollectionVodItem:
                return {
                    infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                    partnerId: item.vodItemKernel.partnerId,
                    unifiedCollectionId: (item.vodItemKernel as UnifiedCollectionVodItemKernel)
                        .unifiedCollectionId,
                } as UnifiedCollectionInfoCardKernel;

            case VodItemType.EpisodeVodItem:
            case VodItemType.MovieVodItem:
            case VodItemType.SpecialVodItem:
                return {
                    infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                    offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                } as VodOfferInfoCardKernel;
            default:
                throw new TypeError(`Unknown VodItemType: The '${type}' is unknown`);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    public getMetaDataForInfoCardActionsOptions(actions: ActionList) {
        if (actions?.actions?.length === 0) {
            return [];
        }

        // Helper function to create OnePass structure
        const createOnePassStructure = (localeKey: string) => ({
            visible: false,
            text: Locale.getString(localeKey),
            actionType: '',
            isMultiOnePass: false,
            kernel: {
                channelId: '',
                channelNumber: '',
                contentId: '',
                isRecordableOffer: false,
                unifiedCollectionId: '',
            },
            title: '',
            value: {
                actionId: '',
                actionKernelType: '',
            },
        });

        const meta = {
            BOOKMARK: {
                visible: false,
                text: '',
                value: {
                    actionType: InfoCardOverlayOptionsType.BOOKMARK,
                    actionKernelType: '',
                    contentId: '',
                    collectionId: '',
                    bookmarkType: '',
                },
            },
            WATCHNOW: {
                visible: false,
                text: Locale.getString(LocaleKeys.PS_OPTION_WATCH_NOW_DETAIL),
                value: {
                    actionType: InfoCardOverlayOptionsType.WATCH_NOW,
                    actionKernelType: '',
                    contentId: '',
                    unifiedCollectionId: '',
                    channelNumber: '',
                },
                providersList: [] as Provider[],
            },
            CREATE_ONEPASS: createOnePassStructure(LocaleKeys.RECORD_OVERLAY_OPTION_CREATE_ONEPASS),
            MODIFY_ONEPASS: createOnePassStructure(LocaleKeys.RECORD_OVERLAY_OPTION_MODIFY_ONEPASS),
            WATCH_FROM_MYSHOWS: {
                visible: false,
                text: Locale.getString(LocaleKeys.RECORD_OVERLAY_OPTION_WATCH_FROM_MY_SHOW),
                value: {
                    actionType: InfoCardOverlayOptionsType.WATCH_FROM_LIVE,
                    actionKernelType: '',
                    contentId: '',
                    recordingId: '',
                    recordingProviderPartnerId: '',
                },
            },
            RECORD_OPTION: {
                visible: false,
                text: '',
                value: {
                    actionType: InfoCardOverlayOptionsType.RECORD_OPTION,
                    actionKernelType: '',
                    contentId: '',
                    offerId: '',
                },
            },
        };

        // --- Helper functions ---
        const setMetaData = <
            K extends keyof Omit<typeof meta, 'CREATE_ONEPASS' | 'MODIFY_ONEPASS'>,
        >(
            key: K,
            update: Partial<(typeof meta)[K]['value']>,
        ) => {
            meta[key].visible = true;
            meta[key].value = { ...meta[key].value, ...update };
        };

        // --- Categorize Kernel types ---
        const bookmarkKernels: ActionKernelType[] = [
            ActionKernelType.BookmarkThisEpisodeActionKernel,
            ActionKernelType.BookmarkThisEventActionKernel,
            ActionKernelType.BookmarkThisGameActionKernel,
            ActionKernelType.BookmarkThisMatchActionKernel,
            ActionKernelType.BookmarkThisMovieActionKernel,
            ActionKernelType.BookmarkThisRaceActionKernel,
            ActionKernelType.BookmarkThisShowActionKernel,
        ];

        const recordKernels: ActionKernelType[] = [
            ActionKernelType.RecordThisShowActionKernel,
            ActionKernelType.RecordThisEpisodeActionKernel,
            ActionKernelType.RecordThisGameActionKernel,
            ActionKernelType.RecordThisMatchActionKernel,
            ActionKernelType.RecordThisEventActionKernel,
            ActionKernelType.RecordThisRaceActionKernel,
            ActionKernelType.RecordThisMovieActionKernel,
        ];

        // --- Main processing ---
        actions.actions.forEach((action) => {
            const { kernel } = action;
            if (!kernel) return;

            const { actionKernelType } = kernel;

            if (bookmarkKernels.includes(actionKernelType)) {
                const k = kernel as {
                    actionId: string;
                    contentId?: string;
                    collectionId?: string;
                };
                const bookmarkKernelType = k.actionId.split('_').pop() ?? '';
                setMetaData('BOOKMARK', {
                    actionKernelType,
                    contentId: k.contentId ?? '',
                    collectionId: k.collectionId ?? '',
                    bookmarkType: bookmarkKernelType,
                });
                meta.BOOKMARK.text = Locale.getString(
                    LocaleKeys[`PS_OPTION_BOOKMARK_THIS_${bookmarkKernelType}`],
                );
                return;
            }

            if (
                actionKernelType === ActionKernelType.WatchLiveActionKernel ||
                actionKernelType === ActionKernelType.WatchFromVodActionKernel
            ) {
                const { providerList } = action as WatchNowAction;
                const k = kernel as WatchLiveActionKernel;
                setMetaData('WATCHNOW', {
                    actionKernelType,
                    channelNumber: k.channelNumber ?? '',
                    contentId: k.actionId ?? '',
                });
                meta.WATCHNOW.providersList = providerList ?? [];
                return;
            }

            if (actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel) {
                const k = kernel as WatchFromMyShowsActionKernel;
                setMetaData('WATCH_FROM_MYSHOWS', {
                    actionKernelType,
                    contentId: k.contentId ?? '',
                    recordingId: k.recordingId,
                    recordingProviderPartnerId: k.recordingProviderPartnerId ?? '',
                });
                return;
            }

            if (
                actionKernelType === ActionKernelType.CreateOnePassActionKernel ||
                actionKernelType === ActionKernelType.CreateOnePassActionKernelV2
            ) {
                const k = kernel as {
                    actionId?: string;
                    channelId?: string;
                    channelNumber?: string;
                    contentId?: string;
                    isRecordableOffer?: boolean;
                    unifiedCollectionId?: string;
                    title?: string;
                };
                meta.CREATE_ONEPASS.visible = true;
                meta.CREATE_ONEPASS.actionType = action.actionType ?? '';
                meta.CREATE_ONEPASS.isMultiOnePass =
                    actionKernelType === ActionKernelType.CreateOnePassActionKernelV2;
                meta.CREATE_ONEPASS.kernel = {
                    channelId: k.channelId ?? '',
                    channelNumber: k.channelNumber ?? '',
                    contentId: k.contentId ?? '',
                    isRecordableOffer: k.isRecordableOffer ?? false,
                    unifiedCollectionId: k.unifiedCollectionId ?? '',
                };
                meta.CREATE_ONEPASS.title = k.title ?? '';
                meta.CREATE_ONEPASS.value = {
                    actionId: k.actionId ?? '',
                    actionKernelType,
                };
                return;
            }

            if (
                actionKernelType === ActionKernelType.ModifyOnePassActionKernel ||
                actionKernelType === ActionKernelType.ModifyOnePassActionKernelV2
            ) {
                const k = kernel as {
                    actionId?: string;
                    channelId?: string;
                    channelNumber?: string;
                    contentId?: string;
                    collectionId?: string;
                    isRecordableOffer?: boolean;
                    unifiedCollectionId?: string;
                    title?: string;
                };
                meta.MODIFY_ONEPASS.visible = true;
                meta.MODIFY_ONEPASS.actionType = action.actionType ?? '';
                meta.MODIFY_ONEPASS.isMultiOnePass =
                    actionKernelType === ActionKernelType.ModifyOnePassActionKernelV2;
                meta.MODIFY_ONEPASS.kernel = {
                    channelId: k.channelId ?? '',
                    channelNumber: k.channelNumber ?? '',
                    contentId: k.contentId ?? '',
                    isRecordableOffer: k.isRecordableOffer ?? false,
                    unifiedCollectionId: k.collectionId ?? k.unifiedCollectionId ?? '',
                };
                meta.MODIFY_ONEPASS.title = k.title ?? '';
                meta.MODIFY_ONEPASS.value = {
                    actionId: k.actionId ?? '',
                    actionKernelType,
                };
                return;
            }

            if (recordKernels.includes(actionKernelType)) {
                const k = kernel as { contentId?: string; offerId?: string; actionId: string };
                const recordKernelType = k.actionId.split('_').pop() ?? '';
                setMetaData('RECORD_OPTION', {
                    actionKernelType,
                    contentId: k.contentId ?? '',
                    offerId: k.offerId ?? '',
                });
                meta.RECORD_OPTION.text = Locale.getString(
                    LocaleKeys[`PS_OPTION_RECORD_THIS_${recordKernelType}`],
                );
            }
        });

        // --- Build final list ---
        const list: InfoCardUserAction[] = [];
        const pushIfVisible = <K extends keyof typeof meta>(key: K) => {
            const item = meta[key];
            if (!item.visible) return;

            if (key === 'CREATE_ONEPASS' || key === 'MODIFY_ONEPASS') {
                const onePassItem = item as
                    | (typeof meta)['CREATE_ONEPASS']
                    | (typeof meta)['MODIFY_ONEPASS'];
                list.push({
                    id: onePassItem.text,
                    visible: true,
                    text: onePassItem.text,
                    actionType: onePassItem.actionType,
                    isMultiOnePass: onePassItem.isMultiOnePass,
                    kernel: { ...onePassItem.kernel },
                    title: onePassItem.title,
                    value: { ...onePassItem.value },
                });
                return;
            }

            const baseAction: InfoCardUserAction = {
                id: item.text,
                visible: true,
                text: item.text,
                value: { ...item.value },
            };

            if (key === 'WATCHNOW') {
                const watchNowItem = item as (typeof meta)['WATCHNOW'];
                list.push({
                    ...baseAction,
                    providersList: watchNowItem.providersList,
                });
            } else {
                list.push(baseAction);
            }
        };

        (Object.keys(meta) as Array<keyof typeof meta>).forEach(pushIfVisible);

        return list;
    }
}
